import type { SearchableComboboxOption } from "@/components/combobox-with-states-4"

import type { UsdPerUnitMap } from "@/lib/currency-convert"
import { countryNamesForCurrency, getCurrencyMeta } from "@/lib/currency-meta"
import { currencyForResidencyCountryIso2, residencyDefaultCurrencyForCountry } from "@/lib/currency-utils"
import type { Profile } from "@/types/profile"

export {
  COUNTRY_CURRENCY_MAP,
  RESIDENCY_COUNTRY_TO_CURRENCY,
  currencyForResidencyCountryIso2,
  residencyDefaultCurrencyForCountry,
} from "@/lib/currency-utils"

export type { UsdPerUnitMap } from "@/lib/currency-convert"

/** ISO 4217 code used across tasks, budgets, and profiles (string, not a fixed union). */
export type AppTaskCurrency = string

export function normalizeIsoCurrency(code: string | undefined | null): string | null {
  const c = (code ?? "").trim().toUpperCase()
  if (c.length !== 3 || !/^[A-Z]{3}$/.test(c)) return null
  return c
}

export type ResolveUserCurrencyOptions = {
  /** BCP 47 tag (e.g. `en-US`, `pt-BR`); region subtag maps via `RESIDENCY_COUNTRY_TO_CURRENCY` in `currency-utils`. */
  navigatorLanguage?: string | null
}

function currencyFromNavigatorLanguage(tag: string | undefined | null): string | null {
  if (!tag || typeof tag !== "string") return null
  const parts = tag.replace(/_/g, "-").split("-").filter(Boolean)
  if (parts.length < 2) return null
  const region = parts[parts.length - 1]!.trim().toUpperCase()
  if (region.length !== 2 || !/^[A-Z]{2}$/.test(region)) return null
  return currencyForResidencyCountryIso2(region)
}

/** Minimal profile fields for {@link resolveUserCurrency}. */
export type ResolveUserCurrencyProfileInput = Pick<Profile, "preferred_currency" | "country_of_residence">

/**
 * Resolves a default ISO currency for the signed-in user context:
 * 1. `profiles.preferred_currency`
 * 2. `profiles.country_of_residence` via `RESIDENCY_COUNTRY_TO_CURRENCY` (unknown country → skip)
 * 3. Browser locale region subtag → same map (BRL only for `BR`, e.g. `pt-BR`)
 * 4. `USD` (never BRL from this chain unless residency or region is Brazil)
 */
export function resolveUserCurrency(
  profile: ResolveUserCurrencyProfileInput | Profile | null,
  options?: ResolveUserCurrencyOptions
): string {
  const pref = normalizeIsoCurrency(profile?.preferred_currency ?? null)
  if (pref) return pref

  const cor = (profile?.country_of_residence ?? "").trim().toUpperCase()
  if (cor.length === 2) {
    const fromResidency = currencyForResidencyCountryIso2(cor)
    if (fromResidency) return normalizeIsoCurrency(fromResidency) ?? fromResidency
  }

  const nav =
    options?.navigatorLanguage ??
    (typeof navigator !== "undefined" && navigator?.language ? navigator.language : null)
  const fromNav = currencyFromNavigatorLanguage(nav)
  if (fromNav) return fromNav

  return "USD"
}

/**
 * True when `code` is a plausible ISO 4217 string; if `usdPerUnit` is set, also requires a rate key.
 */
export function isAppTaskCurrency(
  v: string | undefined | null,
  usdPerUnit?: UsdPerUnitMap | null
): v is AppTaskCurrency {
  const n = normalizeIsoCurrency(v)
  if (!n) return false
  if (!usdPerUnit || Object.keys(usdPerUnit).length === 0) return true
  return typeof usdPerUnit[n] === "number"
}

export type CurrencyOptionRow = {
  value: string
  /** Space-delimited text for search (code, currency name, country names). */
  label: string
  flag: string
  name: string
}

function currencySearchLabel(code: string, currencyName: string): string {
  const countries = countryNamesForCurrency(code)
  return [code, currencyName, ...countries].join(" ").trim()
}

/** Rows from Frankfurter-derived map, sorted A–Z by code. */
export function getCurrencyOptions(usdPerUnit: UsdPerUnitMap): CurrencyOptionRow[] {
  return Object.keys(usdPerUnit)
    .map((k) => k.toUpperCase())
    .filter((k) => k.length === 3)
    .sort()
    .map((value) => {
      const meta = getCurrencyMeta(value)
      const name = meta?.name ?? value
      const flag = meta?.flag ?? "\u{1F310}"
      const label = currencySearchLabel(value, name)
      return { value, label, flag, name }
    })
}

export function getCurrencyComboboxOptions(usdPerUnit: UsdPerUnitMap): SearchableComboboxOption[] {
  return getCurrencyOptions(usdPerUnit).map(({ value, label, flag, name }) => ({
    value,
    label,
    flag,
    name,
    filterText: label,
  }))
}

/**
 * Map raw ISO from country data into a code we can price; prefers keys present in `usdPerUnit` when provided.
 */
export function appTaskCurrencyFromIso4217(
  iso: string | undefined | null,
  usdPerUnit?: UsdPerUnitMap | null
): AppTaskCurrency {
  const c = normalizeIsoCurrency(iso) ?? "USD"
  if (usdPerUnit && typeof usdPerUnit[c] === "number") return c
  if (usdPerUnit && Object.keys(usdPerUnit).length > 0) return "USD"
  return c
}

export function appTaskCurrencyFromCountryCode(
  countryIso2: string | undefined | null,
  usdPerUnit?: UsdPerUnitMap | null
): AppTaskCurrency {
  const code = (countryIso2 ?? "").trim().toUpperCase()
  if (code.length !== 2) return "USD"
  return appTaskCurrencyFromIso4217(residencyDefaultCurrencyForCountry(code), usdPerUnit ?? null)
}

export type ProfileCurrencyContext = {
  country_of_residence: string | null | undefined
  preferred_currency: string | null | undefined
}

export function defaultAppTaskCurrencyFromProfile(
  p: ProfileCurrencyContext | null | undefined,
  usdPerUnit?: UsdPerUnitMap | null
): AppTaskCurrency {
  const resolved = resolveUserCurrency(
    p
      ? {
          country_of_residence: p.country_of_residence ?? null,
          preferred_currency: p.preferred_currency ?? null,
        }
      : null,
    { navigatorLanguage: typeof navigator !== "undefined" ? navigator.language : null }
  )
  const n = normalizeIsoCurrency(resolved) ?? "USD"
  if (usdPerUnit && Object.keys(usdPerUnit).length > 0 && typeof usdPerUnit[n] !== "number") {
    return "USD"
  }
  return n
}
